import { z } from "zod";

const decimalToNumber = z
  .union([z.number(), z.string(), z.null(), z.undefined()])
  .transform((value) => {
    if (value === null || value === undefined) {
      return null;
    }
    if (typeof value === "number") {
      return value;
    }
    const parsed = Number.parseFloat(value);
    return Number.isNaN(parsed) ? null : parsed;
  })
  .nullable()
  .optional();

export const productBrandWarehouseStockSchema = z.object({
  id: z.number(),
  product_brand_id: z.number().nullable().optional(),
  brand_id: z.number().nullable().optional(),
  brand_name: z.string(),
  is_unallocated: z.boolean(),
  warehouse_id: z.number(),
  warehouse_name: z.string(),
  quantity_available: decimalToNumber,
  quantity_reserved: decimalToNumber,
  quantity_available_for_sale: decimalToNumber,
  last_transaction_at: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional().default({}),
  updated_at: z.string(),
});

export type ProductBrandWarehouseStock = z.infer<
  typeof productBrandWarehouseStockSchema
>;

const warehouseProductStockSchema = z.object({
  product_id: z.number(),
  sku: z.string(),
  name: z.string().nullable().optional(),
  storage_type: z.enum(["general", "dry", "cold"]).default("general"),
  uom: z.string().nullable().optional(),
  base_stock_uom_code: z.string().nullable().optional(),
  erp_item_id: z.string().nullable().optional(),
  quantity_available: decimalToNumber,
  quantity_reserved: decimalToNumber,
  quantity_available_for_sale: decimalToNumber,
  last_transaction_at: z.string().nullable().optional(),
  updated_at: z.string(),
  brand_warehouse_stocks: z.array(productBrandWarehouseStockSchema).default([]),
});

const warehouseStockSchema = z.object({
  warehouse_id: z.number(),
  warehouse_name: z.string(),
  warehouse_code: z.string().nullable().optional(),
  warehouse_parent_id: z.number().nullable().optional(),
  warehouse_storage_type: z.enum(["general", "dry", "cold"]).default("general"),
  warehouse_is_default: z.boolean(),
  quantity_available: decimalToNumber,
  quantity_reserved: decimalToNumber,
  quantity_available_for_sale: decimalToNumber,
  last_transaction_at: z.string().nullable().optional(),
  updated_at: z.string(),
});

export const productSchema = z.object({
  id: z.number().default(0),
  sku: z.string(),
  name: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  picture: z.string().nullable().optional(),
  price: decimalToNumber,
  storage_type: z.enum(["general", "dry", "cold"]).default("general"),
  quantity_available: decimalToNumber,
  quantity_reserved: decimalToNumber,
  quantity_available_for_sale: decimalToNumber,
  uom: z.string().nullable().optional(),
  erp_item_id: z.string().nullable().optional(),
  supplier: z.number().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  warehouse_stocks: z.array(warehouseStockSchema).default([])
});

export const warehouseSchema = z.object({
  id: z.number(),
  supplier: z.number(),
  name: z.string(),
  code: z.string().nullable().optional(),
  parent_id: z.number().nullable().optional(),
  parent_name: z.string().nullable().optional(),
  storage_type: z.enum(["general", "dry", "cold"]).default("general"),
  is_stock_location: z.boolean().default(true),
  is_default: z.boolean(),
  is_active: z.boolean(),
  erp_location_id: z.string().nullable().optional(),
  erp_location_name: z.string().nullable().optional(),
  product_stocks: z.array(warehouseProductStockSchema).optional().default([]),
  created_at: z.string(),
  updated_at: z.string(),
});


export const productsSchema = z.array(productSchema);
export const warehousesSchema = z.array(warehouseSchema);

export type Product = z.infer<typeof productSchema>;
export type Warehouse = z.infer<typeof warehouseSchema>;
export type WarehouseStock = z.infer<typeof warehouseStockSchema>;
export type WarehouseProduct = z.infer<typeof warehouseProductStockSchema>;
